import React, { useContext } from "react";
import { Box, InputAdornment, makeStyles } from "@material-ui/core";
import { FormattedMessage, useIntl } from "react-intl";
import { IoIosCalendar, IoIosMail, IoIosPin } from "react-icons/io";
import { FormikHelpers, useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment";

import { FormHeader } from "./FormHeader";
import { InputField } from "../../../components/input-field/InputField";
import { PhoneNumberInput } from "../../../components/phone-number/PhoneNumberInput";
import Country from "../../../models/Country";
import { InputButton } from "../../../components/input-button/InputButton";
import {
  SignupDispatchContext,
  SignupContext,
  SignupTypeEnum,
  SignupPersonalInfoState,
} from "../../../context/SignupContext";
import { Overlay } from "../../../components/overlay/Overlay";
import { UserService } from "../../../services/UserService";
import { base64, formatPhoneNumber, validateFile } from "../../../common/Hooks";
import { ImageChooser } from "../../../components/image-chooser/ImageChooser";
import { PhoneNumber } from "libphonenumber-js/types";
import { InputDate } from "../../../components/input-date/InputDate";
import {
  FILE_MAX_SIZE,
  FILE_MAX_SIZE_STR,
  YYYY_MM_DD_FORMAT,
} from "../../../settings/constants";

interface PersonaAccountFormProps {
  callback: () => void;
}

export const useStyles = makeStyles(
  (theme) => ({
    header: {
      "& .leftWrapper": {
        border: "1px solid red",
      },
    },
  }),
  {
    name: "personal-account-style",
    flip: null,
  }
);

export const PersonaAccountForm = (props: PersonaAccountFormProps) => {
  // Props
  const { callback } = props;

  return (
    <>
      <FormHeader>
        <Box component="div">
          <Box component="span" className="title">
            <FormattedMessage id="sharedScreen.signUp" />
          </Box>
          <Box component="span" className="subTitle">
            <FormattedMessage id="sharedScreen.signUpPersonalAccount" />
          </Box>
        </Box>
      </FormHeader>
      <Form callback={callback} />
    </>
  );
};

export const Form = (props: PersonaAccountFormProps, ...rest: any[]) => {
  const intl = useIntl();
  // Props
  const { callback } = props;
  // Context
  const signupState = useContext(SignupContext);
  const signupDispatch = useContext(SignupDispatchContext);

  const initFormikState = (): SignupPersonalInfoState => {
    return signupState.personal;
  };

  const formikValidation = (): any => {
    return Yup.object().shape({
      email: Yup.string()
        .email(intl.formatMessage({ id: "error.email" }))
        .required(intl.formatMessage({ id: "error.email" })),
      last_name: Yup.string().required(
        intl.formatMessage({ id: "error.enterName" })
      ),
      first_name: Yup.string().required(
        intl.formatMessage({ id: "error.enterName" })
      ),
      data_of_birth: Yup.string().required(
        intl.formatMessage({ id: "error.dateOfBirth" })
      ),
      birth_place: Yup.string().required(
        intl.formatMessage({ id: "error.placeOfBirth" })
      ),
      address: Yup.string().required(
        intl.formatMessage({ id: "error.address" })
      ),
      profile_picture: Yup.mixed().test(
        "fileSize",
        intl.formatMessage(
          { id: "error.tooLargePicture" },
          { max: FILE_MAX_SIZE_STR }
        ),
        (value) => {
          if (value === null) return true;
          return validateFile(value, FILE_MAX_SIZE);
        }
      ),
    });
  };

  const canWeFollowStep = (
    errors: Array<string>,
    stateKeys: Array<string>
  ): boolean => {
    for (let i = 0; i < stateKeys.length; i++) {
      let element = stateKeys[i];
      if (errors.indexOf(element) !== -1) return false;
    }
    return true;
  };

  const handleAvatarSelection = (file: any) => {
    if (file !== null) {
      base64(file, (result) => {
        formik.values.profile_picture = result;
      });
    } else {
      formik.values.profile_picture = null;
    }
    formik.setErrors({ ...formik.errors, profile_picture: undefined });
  };

  const genericError = () => {
    if (formik.errors.unknown) {
      return formik.errors.unknown as string;
    }

    return "";
  };

  const handlePhoneChange = (phoneNumber: PhoneNumber | null) => {
    if (phoneNumber !== null) {
      formik.values.secondary_telephone = phoneNumber.number.toString();
      formik.values.secondary_dial_code = `+${phoneNumber.countryCallingCode}`;
      formik.values.secondary_country_iso_code = phoneNumber.country
        .toString()
        .toUpperCase();

      if (!phoneNumber.isValid()) {
        formik.setErrors({
          ...formik.errors,
          secondary_telephone: intl.formatMessage({
            id: "error.invalidPhoneNumber",
          }),
        });
      } else {
        formik.setErrors({ ...formik.errors, secondary_telephone: undefined });
      }
    } else {
      formik.values.secondary_telephone = "";
      formik.setErrors({ ...formik.errors, secondary_telephone: undefined });
    }
  };

  const handleDateChange = (name: string, dateString: string) => {
    formik.setValues({ ...formik.values, [name]: dateString });
  };

  const formik = useFormik({
    initialValues: initFormikState(),
    validationSchema: formikValidation(),
    onSubmit: async (
      values: SignupPersonalInfoState,
      { setErrors, setSubmitting }: FormikHelpers<any>
    ) => {
      try {
        const response = await UserService.register(values);
        setSubmitting(false);

        if (response.isSuccess) {
          switch (response.data.code) {
            case 150: // Validation error
              let errors = JSON.parse(JSON.stringify(response.data.data));
              let stateObjectKeys = Object.keys(signupState.personal);

              if (canWeFollowStep(Object.keys(errors), stateObjectKeys)) {
                signupDispatch({
                  type: SignupTypeEnum.store,
                  payload: {
                    personal: {
                      ...signupState.personal,
                      ...formik.values,
                      profile_picture: formik.values.profile_picture,
                    },
                  },
                });

                callback();
              } else {
                setErrors(errors);
              }
              break;
            case 102: // Unable to check your phone number verification
              setErrors({ unknown: response.data.message });
              break;
          }
        } else {
          setErrors({
            unknown: intl.formatMessage({ id: "sharedScreen.unknownError" }),
          });
        }
      } catch (err) {
        setSubmitting(false);
        setErrors({
          unknown: intl.formatMessage({ id: "sharedScreen.unknownError" }),
        });
      }
    },
  });

  return (
    <>
      {formik.errors ? (
        <Box mt={4} component="div" className="errorWrapper" textAlign="center">
          {genericError()}
        </Box>
      ) : (
        <></>
      )}

      <Box component="div" mt={6}>
        <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
          <Box component="div" mt={6} mb={8}>
            <Box
              component="div"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <ImageChooser
                size="large"
                defaultFile={formik.values.profile_picture}
                onImageSelected={handleAvatarSelection}
              />
            </Box>
            {formik.errors.profile_picture ? (
              <Box
                mt={4}
                component="div"
                className="errorWrapper"
                textAlign="center"
              >
                {formik.errors.profile_picture as string}
              </Box>
            ) : (
              <></>
            )}
          </Box>

          <Box component="div">
            <Box
              className="inlineForm"
              mb={2}
              display="flex"
              flexWrap="nowrap"
              alignItems="start"
            >
              <Box className="inlineFormElement">
                <InputField
                  fullWidth
                  id="first_name"
                  name="first_name"
                  label={intl.formatMessage({ id: "sharedScreen.firstName" })}
                  placeholder={intl.formatMessage({
                    id: "sharedScreen.enterFirstName",
                  })}
                  type="text"
                  value={formik.values.first_name}
                  onChange={formik.handleChange}
                  error={
                    Boolean(formik.touched.first_name) &&
                    Boolean(formik.errors.first_name)
                  }
                  helperText={
                    formik.touched.first_name && formik.errors.first_name
                  }
                />
              </Box>
              <Box width={24} />
              <Box className="inlineFormElement">
                <InputField
                  fullWidth
                  id="last_name"
                  name="last_name"
                  label={intl.formatMessage({ id: "sharedScreen.lastName" })}
                  placeholder={intl.formatMessage({
                    id: "sharedScreen.enterLastName",
                  })}
                  type="text"
                  value={formik.values.last_name}
                  onChange={formik.handleChange}
                  error={
                    Boolean(formik.touched.last_name) &&
                    Boolean(formik.errors.last_name)
                  }
                  helperText={
                    formik.touched.last_name && formik.errors.last_name
                  }
                />
              </Box>
            </Box>

            <Box mb={2}>
              <InputField
                fullWidth
                id="email"
                name="email"
                label={intl.formatMessage({ id: "sharedScreen.email" })}
                placeholder={intl.formatMessage({
                  id: "sharedScreen.enterEmail",
                })}
                type="text"
                endAdornment={
                  <InputAdornment position="end">
                    <IoIosMail />
                  </InputAdornment>
                }
                value={formik.values.email}
                onChange={formik.handleChange}
                error={
                  Boolean(formik.touched.email) && Boolean(formik.errors.email)
                }
                helperText={formik.touched.email && formik.errors.email}
              />
            </Box>
            <Box
              className="inlineForm"
              display="flex"
              flexWrap="nowrap"
              alignItems="start"
            >
              <Box className="inlineFormElement">
                <InputDate
                  maxDate={moment()
                    .subtract(10, "years")
                    .format(YYYY_MM_DD_FORMAT)}
                  onDateChange={(name, date) => handleDateChange(name, date)}
                  inputProps={{
                    fullWidth: true,
                    id: "data_of_birth",
                    name: "data_of_birth",
                    label: intl.formatMessage({ id: "sharedScreen.birthDate" }),
                    placeholder: intl.formatMessage({
                      id: "sharedScreen.enterBirthDate",
                    }),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IoIosCalendar />
                      </InputAdornment>
                    ),

                    value: formik.values.data_of_birth,
                    error:
                      Boolean(formik.touched.data_of_birth) &&
                      Boolean(formik.errors.data_of_birth),
                    helperText:
                      formik.touched.data_of_birth &&
                      formik.errors.data_of_birth,
                  }}
                  pickerProps={{
                    format: YYYY_MM_DD_FORMAT,
                    disabled: false,
                    showToday: false,
                  }}
                />
              </Box>
              <Box width={24} />
              <Box className="inlineFormElement">
                <InputField
                  fullWidth
                  id="birth_place"
                  name="birth_place"
                  label={intl.formatMessage({ id: "sharedScreen.birthPlace" })}
                  placeholder={intl.formatMessage({
                    id: "sharedScreen.enterBirthPlace",
                  })}
                  type="text"
                  value={formik.values.birth_place}
                  onChange={formik.handleChange}
                  error={
                    Boolean(formik.touched.birth_place) &&
                    Boolean(formik.errors.birth_place)
                  }
                  helperText={
                    formik.touched.birth_place && formik.errors.birth_place
                  }
                />
              </Box>
            </Box>

            <Box>
              <InputField
                fullWidth
                id="address"
                name="address"
                label={intl.formatMessage({ id: "sharedScreen.address" })}
                placeholder={intl.formatMessage({
                  id: "sharedScreen.enterAddress",
                })}
                type="text"
                endAdornment={
                  <InputAdornment position="end">
                    <IoIosPin />
                  </InputAdornment>
                }
                value={formik.values.address}
                onChange={formik.handleChange}
                error={
                  Boolean(formik.touched.address) &&
                  Boolean(formik.errors.address)
                }
                helperText={formik.touched.address && formik.errors.address}
              />
            </Box>
            <Box mt={4}>
              <PhoneNumberInput
                id="telephone"
                name="telephone"
                label={intl.formatMessage({
                  id: "sharedScreen.primaryPhoneNumber",
                })}
                disabled={true}
                defaultPhone={formatPhoneNumber(formik.values.telephone, false)}
                defaultCountry={
                  new Country(
                    "",
                    signupState.personal.dial_code,
                    signupState.personal.country_iso_code.toLowerCase()
                  )
                }
                error={formik.touched.telephone && formik.errors.telephone}
              />
            </Box>
            <Box mt={2}>
              <PhoneNumberInput
                id="secondary_telephone"
                name="secondary_telephone"
                label={intl.formatMessage({
                  id: "sharedScreen.secondaryPhoneNumber",
                })}
                defaultCountry={
                  formik.values.secondary_telephone
                    ? new Country(
                        "",
                        formik.values.secondary_dial_code,
                        formik.values.secondary_country_iso_code.toLowerCase()
                      )
                    : Country.defaultCountry("")
                }
                defaultPhone={formatPhoneNumber(
                  formik.values.secondary_telephone,
                  false
                )}
                error={
                  formik.touched.secondary_telephone &&
                  formik.errors.secondary_telephone
                }
                onChange={handlePhoneChange}
              />
            </Box>

            <Box
              mt={10}
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
            >
              <Box width={100}>
                <InputButton
                  color="secondary"
                  fullWidth
                  size="medium"
                  variant="contained"
                  type="submit"
                >
                  <FormattedMessage id="sharedScreen.next" />
                </InputButton>
              </Box>
            </Box>
          </Box>
        </form>
      </Box>

      <Overlay loading={formik.isSubmitting} />
    </>
  );
};
